import { DisplayFormat, mapEntity, SnakeMeta } from '@api';
import { ChartVariant, PriceHoverData } from '@chart';
import { globalPriceFormatter } from '@terminal/utils/asset-price/assetPrice';
import { useTranslation } from 'react-i18next';

import { useChartTheme } from '../../hooks/use-chart-theme/useChartTheme';
import type { TerminalChartAssetData } from '../../utils/chart-utils/types';
import styles from './IndicatorLegendDetails.module.scss';

const colorRecord: Record<string, string> = {
  positive: 'var(--viz-bullish)',
  negative: 'var(--viz-bearish)',
  neutral: 'var(--viz-neutral)',
  info: 'var(--viz-info)',
  warning: 'var(--viz-warning)',
};

export interface IndicatorLegendDetailsProps {
  chartAssetData: TerminalChartAssetData;
  hoverData?: PriceHoverData;
}

export const IndicatorLegendDetails = ({
  chartAssetData,
  hoverData,
}: IndicatorLegendDetailsProps) => {
  const { t } = useTranslation('chart');
  const { colors } = useChartTheme();
  const entity = mapEntity(chartAssetData.entity);
  const snakeMeta = chartAssetData.snakeMeta as SnakeMeta;
  const threshold = snakeMeta.threshold;
  const color = colors.getThemeColor(chartAssetData.lineColorToken);

  const priceFormatter = (price: number) =>
    globalPriceFormatter({
      entity,
      price,
      priceSuffix: entity.currency,
      shouldShowCurrency: snakeMeta.display_format === DisplayFormat.CURRENCY,
    });

  const thresholdLinesData = threshold
    ? [
        {
          key: 'positive' as ChartVariant,
          value: threshold.good,
          indicatorLine: <span className={styles.positiveLine} />,
        },
        {
          key: 'negative' as ChartVariant,
          value: threshold.bad,
          indicatorLine: (
            <div className={styles.negativeIndicator}>
              <span className={styles.negativeLine} />{' '}
              <span className={styles.negativeLine} />
              <span className={styles.negativeLine} />
            </div>
          ),
        },
      ]
    : [];

  const lastClosePrice = chartAssetData.ts[chartAssetData.ts.length - 1]?.close;
  const hoveredClosePrice = hoverData?.currentPrice.close;
  const closePrice = hoveredClosePrice ?? lastClosePrice;

  return (
    <div className={styles.container} data-testid="indicator-legend-details">
      <span
        data-testid="indicator-legend-price"
        className={styles.value}
        style={{ color }}
      >
        {priceFormatter(closePrice)}
      </span>
      {thresholdLinesData
        .filter(item => typeof item.value === 'number')
        .map(item => (
          <div
            data-testid={`threshold-${item.key}`}
            key={item.key}
            className={styles.item}
          >
            {item.indicatorLine}
            <p className={styles.label}>
              {t('chart:threshold', { context: item.key })}
            </p>
            <span
              className={styles.value}
              style={{ color: colorRecord[item.key] }}
            >
              {item.value}
            </span>
          </div>
        ))}
    </div>
  );
};
